<script setup>
import { nextTick, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ArrowPathIcon, ClockIcon, SignalIcon } from '@heroicons/vue/24/outline'
import { CheckCircleIcon } from '@heroicons/vue/24/solid'
import Pts2AlertsPanel from '../components/pts2/Pts2AlertsPanel.vue'
import Pts2ControllerHealth from '../components/pts2/Pts2ControllerHealth.vue'
import Pts2ControllerSelector from '../components/pts2/Pts2ControllerSelector.vue'
import Pts2PumpCards from '../components/pts2/Pts2PumpCards.vue'
import Pts2TankList from '../components/pts2/Pts2TankList.vue'
import { usePts2CurrentStatus } from '../pts2/context.js'
import { useI18n } from '../i18n.js'

const { locale, t } = useI18n()

const controllerSelectorRegion = ref(null)
const alertRegion = ref(null)
const refreshAction = ref(null)
const router = useRouter()
const {
  context,
  dashboard,
  loading,
  switching,
  refreshing,
  error,
  noCurrentData,
  announcement,
  initialize,
  refresh,
  selectController,
} = usePts2CurrentStatus()

let restoreRefreshFocus = false
watch(refreshing, async (busy) => {
  if (busy) {
    restoreRefreshFocus = document.activeElement === refreshAction.value
    return
  }
  if (!restoreRefreshFocus) return
  restoreRefreshFocus = false
  await nextTick()
  if (document.activeElement === document.body && refreshAction.value && !refreshAction.value.disabled) refreshAction.value.focus()
}, { flush: 'sync' })

function time(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(locale.value, {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(date)
}

async function focusAlerts() {
  announcement.value = t('pts2UiAlertsShown', { count: dashboard.value.alerts.length })
  await nextTick()
  alertRegion.value?.focus()
}

async function focusControllers() {
  await router.push({ name: 'pts2-controllers' })
}
</script>

<template>
  <section class="pts2-current-page">
    <h1>{{ t('pts2UiCurrent') }}</h1>
    <p class="visually-hidden" aria-live="polite">{{ announcement }}</p>

    <div ref="controllerSelectorRegion" class="pts2-controller-bar">
      <Pts2ControllerSelector :controllers="context.controllers" :active-controller-id="context.activeControllerId"
        :disabled="loading || switching" @select="selectController" />
      <span class="pts2-controller-fact pts2-connection" :class="dashboard.controller.freshness.key">
        <CheckCircleIcon class="pts2-status-icon" aria-hidden="true" />
        <strong>{{ t(`pts2UiFreshness_${dashboard.controller.freshness.key}`) }}</strong>
      </span>
      <span class="pts2-controller-fact">
        <ClockIcon aria-hidden="true" />
        <span><small>{{ t('pts2UiLastData') }}</small><strong>{{ time(dashboard.controller.receivedAt) }}</strong></span>
      </span>
      <span class="pts2-controller-fact pts2-transport">
        <SignalIcon aria-hidden="true" /><strong>{{ dashboard.controller.transport || '—' }}</strong>
      </span>
      <button ref="refreshAction" class="pts2-refresh-action" type="button"
        :disabled="refreshing || loading || !context.activeControllerId" @click="refresh">
        <ArrowPathIcon :class="{ spinning: refreshing }" aria-hidden="true" />
        {{ t(refreshing ? 'pts2UiRefreshing' : 'pts2UiRefresh') }}
      </button>
    </div>

    <p v-if="error" class="notice error pts2-page-notice" role="alert">
      {{ error }} <button class="inline-retry" type="button" @click="initialize">{{ t('pts2UiRetry') }}</button>
    </p>
    <div v-if="loading" class="pts2-loading" role="status">
      <ArrowPathIcon class="spinning" aria-hidden="true" />
      <span>{{ t('pts2UiLoadingStatus') }}</span>
    </div>
    <div v-else-if="!context.controllers.length" class="pts2-empty-state">
      <strong>{{ t('pts2UiNoStationControllers') }}</strong>
      <p>{{ t('pts2UiStationControllerHelp') }}</p>
    </div>
    <div v-else-if="context.selectionRequired && !context.activeControllerId" class="pts2-empty-state">
      <strong>{{ t('pts2UiChooseController') }}</strong>
      <p>{{ t('pts2UiSourceIsolation') }}</p>
    </div>
    <div v-else-if="noCurrentData" class="pts2-empty-state" role="status">
      <strong>{{ t('pts2UiNoCurrentData') }}</strong>
    </div>
    <div v-else-if="context.activeControllerId" class="pts2-dashboard-grid">
      <section class="pts2-main-card" aria-labelledby="pts2-pumps-title">
        <h2 id="pts2-pumps-title">{{ t('pts2UiCategory_PUMP') }}</h2>
        <Pts2PumpCards :pumps="dashboard.pumps" />
        <h2 id="pts2-tanks-title" class="pts2-tanks-heading">{{ t('pts2UiTanks') }}</h2>
        <Pts2TankList :tanks="dashboard.tanks" />
      </section>
      <aside class="pts2-side-stack">
        <div ref="alertRegion" tabindex="-1"><Pts2AlertsPanel :alerts="dashboard.alerts" @show-all="focusAlerts" /></div>
        <Pts2ControllerHealth :health="dashboard.health" @show-controllers="focusControllers" />
      </aside>
    </div>
  </section>
</template>

<style scoped>
.pts2-current-page :deep(button:focus-visible), .pts2-current-page :deep(a:focus-visible) { outline: 3px solid #26899a; outline-offset: 3px; }
</style>
